import React, { useState, useEffect, useCallback } from 'react';
import { admin } from '../services/api';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import { useAuth } from '../contexts/AuthContext';
import { useHotkeys } from 'react-hotkeys-hook';
import { Tooltip } from 'react-tooltip';
import OrganizationsPanel from './OrganizationsPanel';
import PasswordRequirements, { isPasswordValid } from './PasswordRequirements';

const AdminDashboard = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [dids, setDids] = useState([]);
  const [tempSmsLimits, setTempSmsLimits] = useState({});
  const [tempStartDates, setTempStartDates] = useState({});
  const [newUser, setNewUser] = useState({ 
    username: '', 
    password: '', 
    role: 'user',
    smsLimit: 1000 // Default SMS limit
  });
  const [newDid, setNewDid] = useState({ number: '' });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ userId: '', newPassword: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteDidConfirm, setShowDeleteDidConfirm] = useState(false);
  const [didToDelete, setDidToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 20;
  const [smsStartDate, setSmsStartDate] = useState('');
  const [smsResetDate, setSmsResetDate] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [smsLimit, setSmsLimit] = useState('');

  // Keyboard shortcuts
  useHotkeys('ctrl+n', (e) => {
    e.preventDefault();
    document.getElementById('newUserUsername').focus();
  });

  useHotkeys('ctrl+d', (e) => {
    e.preventDefault();
    document.getElementById('newDidNumber').focus();
  });

  useHotkeys('esc', () => {
    setShowChangePasswordModal(false);
    setShowDeleteConfirm(false);
    setShowDeleteDidConfirm(false);
  });

  const loadData = useCallback(async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      const [usersResponse, didsResponse] = await Promise.all([
        admin.listUsers({ page: pageNum, limit: ITEMS_PER_PAGE }),
        admin.listDIDs()
      ]);
      
      if (append) {
        setUsers(prev => [...prev, ...usersResponse.data]);
      } else {
        setUsers(usersResponse.data);
      }
      
      // Process DIDs to ensure consistent assignment status
      const processedDids = didsResponse.data.map(did => {
        // Flag to track if this DID is referenced by any user
        let isAssignedInUsers = false;
        let assignedToUser = null;
        
        // Check all users to see if any reference this DID
        usersResponse.data.forEach(user => {
          if (user.assignedDID && 
              (user.assignedDID._id === did._id || 
               String(user.assignedDID._id) === String(did._id))) {
            isAssignedInUsers = true;
            assignedToUser = user._id;
          }
        });
        
        // Return enhanced DID with consistent assignment info
        return {
          ...did,
          // If DID is referenced by a user but assignedTo is not set, fix it
          assignedTo: did.assignedTo || (isAssignedInUsers ? assignedToUser : null),
          isAssignedInUsers, // Add this flag for more reliable filtering
          // Add the DID number to easily identify which users have this DID assigned
          assignedToUsername: (() => {
            const assignedUser = usersResponse.data.find(
              user => user.assignedDID && 
                (user.assignedDID._id === did._id || 
                 String(user.assignedDID._id) === String(did._id))
            );
            return assignedUser ? assignedUser.username : null;
          })()
        };
      });
      
      console.log('Processed DIDs with user assignment checks:', 
        processedDids.map(d => ({
          number: d.number, 
          assignedTo: d.assignedTo,
          isAssignedInUsers: d.isAssignedInUsers,
          assignedToUsername: d.assignedToUsername
        }))
      );
      
      setDids(processedDids);
      setHasMore(usersResponse.data.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (dids.length > 0) {
      console.log('DIDs loaded:', dids.length);
      logDIDState();
    }
  }, [dids]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    // The server enforces this too; catching it here avoids a pointless round
    // trip and shows the failure next to the field.
    if (!isPasswordValid(newUser.password)) {
      toast.error('Password does not meet the requirements shown below the field');
      return;
    }
    try {
      setActionLoading(prev => ({ ...prev, createUser: true }));
      await admin.createUser(newUser);
      setNewUser({ username: '', password: '', role: 'user', smsLimit: 1000 });
      toast.success('User created successfully');
      loadData();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.error || 'Failed to create user');
    } finally {
      setActionLoading(prev => ({ ...prev, createUser: false }));
    }
  };

  const handleCreateDID = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(prev => ({ ...prev, createDid: true }));
      await admin.createDID(newDid);
      setNewDid({ number: '' });
      toast.success('DID created successfully');
      loadData();
    } catch (error) {
      console.error('Error creating DID:', error);
      toast.error(error.response?.data?.error || 'Failed to create DID');
    } finally {
      setActionLoading(prev => ({ ...prev, createDid: false }));
    }
  };

  const handleAssignDID = async (userId, didId) => {
    try {
      setActionLoading(prev => ({ ...prev, [`assign_${userId}`]: true }));
      await admin.updateUser(userId, { assignedDID: didId });
      toast.success('DID assigned successfully');
      loadData();
    } catch (error) {
      console.error('Error assigning DID:', error);
      toast.error(error.response?.data?.error || 'Failed to assign DID');
    } finally {
      setActionLoading(prev => ({ ...prev, [`assign_${userId}`]: false }));
    }
  };

  const handleUnassignDID = async (userId) => {
    if (!window.confirm('Are you sure you want to unassign this DID?')) return;
    try {
      setActionLoading(prev => ({ ...prev, [`unassign_${userId}`]: true }));
      await admin.updateUser(userId, { assignedDID: null });
      toast.success('DID unassigned successfully');
      loadData();
    } catch (error) {
      console.error('Error unassigning DID:', error);
      toast.error(error.response?.data?.error || 'Failed to unassign DID');
    } finally {
      setActionLoading(prev => ({ ...prev, [`unassign_${userId}`]: false }));
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      setActionLoading(prev => ({ ...prev, [`status_${userId}`]: true }));
      await admin.updateUser(userId, { isActive: !currentStatus });
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      loadData();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error(error.response?.data?.error || 'Failed to update user status');
    } finally {
      setActionLoading(prev => ({ ...prev, [`status_${userId}`]: false }));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(prev => ({ ...prev, changePassword: true }));
      await admin.updateUser(passwordData.userId, { password: passwordData.newPassword });
      toast.success('Password updated successfully');
      setShowChangePasswordModal(false);
      setPasswordData({ userId: '', newPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password');
    } finally {
      setActionLoading(prev => ({ ...prev, changePassword: false }));
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setActionLoading(prev => ({ ...prev, [`delete_${userId}`]: true }));
      await admin.deleteUser(userId);
      toast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_${userId}`]: false }));
    }
  };

  const handleDeleteDID = async (number) => {
    try {
      setActionLoading(prev => ({ ...prev, [`deleteDid_${number}`]: true }));
      await admin.deleteDID(number);
      toast.success('DID deleted successfully');
      setShowDeleteDidConfirm(false);
      setDidToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting DID:', error);
      toast.error(error.response?.data?.error || 'Failed to delete DID');
    } finally {
      setActionLoading(prev => ({ ...prev, [`deleteDid_${number}`]: false }));
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadData(nextPage, true);
    }
  };

  const handleSmsLimitChange = (userId, value) => {
    setTempSmsLimits(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  const handleStartDateChange = (userId, value) => {
    setTempStartDates(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  const handleUpdateSMSLimit = async (userId) => {
    try {
      if (!userId) {
        toast.error('Invalid user ID');
        return;
      }
      
      setActionLoading(prev => ({ ...prev, [`smsLimit_${userId}`]: true }));
      
      const limitValue = tempSmsLimits[userId] === '' ? null : parseInt(tempSmsLimits[userId]);
      const startDate = tempStartDates[userId] || formatDate(new Date());
      
      // Always include smsStartDate when updating smsLimit
      const updateData = {
        smsLimit: limitValue,
        smsStartDate: limitValue !== null ? startDate : null
      };
      
      console.log('Updating SMS limit with data:', updateData);
      
      const response = await admin.updateUser(userId, updateData);
      
      if (response.data.success) {
        console.log('SMS limit update response:', response.data);
        
        setUsers(users.map(user => 
          user._id === userId ? response.data.user : user
        ));
        
        // Clear the temporary values after successful update
        setTempSmsLimits(prev => {
          const updated = { ...prev };
          delete updated[userId];
          return updated;
        });
        setTempStartDates(prev => {
          const updated = { ...prev };
          delete updated[userId];
          return updated;
        });
        
        toast.success('SMS limit updated successfully');
      }
    } catch (error) {
      console.error('Error updating SMS limit:', error);
      toast.error(error.response?.data?.message || 'Failed to update SMS limit');
    } finally {
      setActionLoading(prev => ({ ...prev, [`smsLimit_${userId}`]: false }));
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  };

  const formatDisplayDate = (date) => {
    if (!date) return 'Not set (Unlimited)';
    
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid date';
    
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSMSLimitSubmit = async (userId) => {
    try {
      const response = await admin.updateUser(userId, {
        smsLimit: smsLimit === '' ? null : parseInt(smsLimit),
        smsStartDate: smsStartDate || null
      });
      
      if (response.data.success) {
        setUsers(users.map(user => 
          user._id === userId ? { ...user, 
            smsLimit: response.data.user.smsLimit,
            smsStartDate: response.data.user.smsStartDate,
            smsResetDate: response.data.user.smsResetDate
          } : user
        ));
        setSmsLimit('');
        setSmsStartDate('');
        setSmsResetDate('');
        setSelectedUserId(null);
        toast.success('SMS limit updated successfully');
      }
    } catch (error) {
      console.error('Error updating SMS limit:', error);
      toast.error(error.response?.data?.message || 'Error updating SMS limit');
    }
  };

  const logDIDState = () => {
    console.log('Current DIDs state:', dids.map(did => ({
      id: did._id,
      number: did.number,
      assignedTo: did.assignedTo
    })));
  };

  // Add this new function to fix orphaned DID assignments
  const handleFixOrphanedDID = async (didId, didNumber) => {
    try {
      setActionLoading(prev => ({ ...prev, [`fixDid_${didNumber}`]: true }));
      
      // First check if any user has this DID assigned
      const userWithThisDID = users.find(user => 
        user.assignedDID && (user.assignedDID._id === didId || String(user.assignedDID._id) === String(didId))
      );
      
      if (userWithThisDID) {
        toast.info(`DID ${didNumber} is actually assigned to user ${userWithThisDID.username}`);
      } else {
        // If no user has this DID, then clear the assignedTo field in the DID
        console.log(`Fixing orphaned DID ${didNumber}`);
        await admin.updateDID(didNumber, { assignedTo: null });
        toast.success(`Fixed orphaned DID ${didNumber}`);
      }
      
      // Reload data to reflect changes
      loadData();
    } catch (error) {
      console.error(`Error fixing orphaned DID ${didNumber}:`, error);
      toast.error(`Failed to fix DID ${didNumber}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [`fixDid_${didNumber}`]: false }));
    }
  };

  // Add this new function to repair all DIDs
  const handleRepairAllDIDs = async () => {
    try {
      setActionLoading(prev => ({ ...prev, repairAllDIDs: true }));
      const response = await admin.repairDIDAssignments();
      console.log('Repair all DIDs response:', response.data);
      toast.success('All DID assignments repaired successfully');
      loadData();
    } catch (error) {
      console.error('Error repairing DIDs:', error);
      toast.error('Failed to repair DID assignments');
    } finally {
      setActionLoading(prev => ({ ...prev, repairAllDIDs: false }));
    }
  };

  if (loading && page === 1) {
    return (
      <div>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      <Navbar />
      {/* index.css disables page scrolling (html/body/#root are overflow:hidden),
          so this pane is the dashboard's scroll container. Without it, anything
          below the fold is simply unreachable. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumbs */}
        <nav className="mb-8">
          <ol className="flex text-gray-500">
            <li>Dashboard</li>
            <li className="mx-2">/</li>
            <li className="text-gray-900">Admin Panel</li>
          </ol>
        </nav>

        {/* Organizations: shared DIDs and pooled SMS allowance */}
        <OrganizationsPanel
          users={users}
          dids={dids}
          onChanged={() => loadData(1, false)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Users Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">Users</h2>
            
            {/* Create User Form */}
            <form onSubmit={handleCreateUser} className="mb-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  id="newUserUsername"
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                  data-tooltip-id="username-tooltip"
                  data-tooltip-content="Use only letters, numbers, and underscores"
                />
                <Tooltip id="username-tooltip" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <PasswordRequirements password={newUser.password} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Monthly SMS Limit</label>
                <input
                  type="number"
                  min="0"
                  value={newUser.smsLimit}
                  onChange={(e) => setNewUser({ ...newUser, smsLimit: parseInt(e.target.value) })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={actionLoading.createUser}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {actionLoading.createUser ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Create User'
                )}
              </button>
            </form>

            {/* Users List */}
            <div className="space-y-4">
              {users.map((user) => (
                <div key={user._id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold">{user.username}</h3>
                      <p className="text-sm text-gray-500">Role: {user.role}</p>
                      <p className="text-sm text-gray-500">
                        SMS Usage: {user.smsUsage?.monthly || 0} / {user.smsLimit ? user.smsLimit : 'Unlimited'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {(() => {
                          // Access comes from the organization, not from a personal
                          // assignment, so show the numbers this user can actually use.
                          const orgDids = user.organization
                            ? dids.filter(d => String(d.organization) === String(user.organization))
                            : [];
                          if (orgDids.length) {
                            return <>DID: {orgDids.map(d => d.number).join(', ')} <span className="text-gray-400">(via organization)</span></>;
                          }
                          return <>DID: {user.assignedDID?.number || 'None'}</>;
                        })()}
                      </p>
                      <p className="text-sm text-gray-500">
                        Status: {user.isActive ? 'Active' : 'Deactivated'}
                      </p>
                      {/* Always show cycle dates, even for unlimited users */}
                      <p className="text-sm text-gray-500">Cycle Start: {formatDisplayDate(user.smsStartDate)}</p>
                      <p className="text-sm text-gray-500">Next Reset: {formatDisplayDate(user.smsResetDate)}</p>
                    </div>
                    <div className="flex space-x-2">
                      {
                        <>
                          <button
                            onClick={() => {
                              setPasswordData({ userId: user._id, newPassword: '' });
                              setShowChangePasswordModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            data-tooltip-id="change-password-tooltip"
                            data-tooltip-content="Change Password"
                            disabled={actionLoading[`password_${user._id}`]}
                          >
                            <i className="fas fa-key text-lg"></i>
                          </button>
                          <Tooltip id="change-password-tooltip" />
                          
                          <button
                            onClick={() => handleToggleUserStatus(user._id, user.isActive)}
                            className={`${user.isActive ? 'text-yellow-600 hover:text-yellow-800' : 'text-green-600 hover:text-green-800'} p-1`}
                            data-tooltip-id="toggle-status-tooltip"
                            data-tooltip-content={user.isActive ? 'Deactivate User' : 'Activate User'}
                            disabled={actionLoading[`status_${user._id}`]}
                          >
                            {actionLoading[`status_${user._id}`] ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                            ) : (
                              <i className={`fas ${user.isActive ? 'fa-ban' : 'fa-check-circle'} text-lg`}></i>
                            )}
                          </button>
                          <Tooltip id="toggle-status-tooltip" />
                          
                          <button
                            onClick={() => {
                              setUserToDelete(user);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            data-tooltip-id="delete-user-tooltip"
                            data-tooltip-content="Delete User"
                            disabled={actionLoading[`delete_${user._id}`]}
                          >
                            {actionLoading[`delete_${user._id}`] ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                            ) : (
                              <i className="fas fa-trash text-lg"></i>
                            )}
                          </button>
                          <Tooltip id="delete-user-tooltip" />
                        </>
                      }
                    </div>
                  </div>
                  {user.role !== 'admin' && (
                    <div className="mt-2">
                      {user.organization && dids.some(d => String(d.organization) === String(user.organization)) ? (
                        <span className="text-xs text-gray-400">
                          Numbers are shared through the organization — manage them in the Organizations panel above.
                        </span>
                      ) : user.assignedDID ? (
                        <button
                          onClick={() => handleUnassignDID(user._id)}
                          className="text-sm text-red-600 hover:text-red-800"
                          disabled={actionLoading[`unassign_${user._id}`]}
                        >
                          {actionLoading[`unassign_${user._id}`] ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 inline-block mr-2"></div>
                          ) : null}
                          Unassign DID
                        </button>
                      ) : (
                        <div>
                          <select
                            onChange={(e) => handleAssignDID(user._id, e.target.value)}
                            className="text-sm border rounded px-2 py-1"
                            defaultValue=""
                            disabled={actionLoading[`assign_${user._id}`]}
                          >
                            <option value="" disabled>
                              {actionLoading[`assign_${user._id}`] ? 'Assigning...' : 'Assign DID'}
                            </option>
                            {dids
                              .filter(did => {
                                // Check both the assignedTo property and our custom isAssignedInUsers flag
                                const isAvailable = !did.assignedTo && !did.isAssignedInUsers;
                                
                                // Log filtering results for debugging
                                if (!isAvailable) {
                                  console.log(`DID ${did.number} is not available - assigned to: ${did.assignedToUsername || 'unknown user'}`);
                                } else {
                                  console.log(`DID ${did.number} is available for assignment`);
                                }
                                
                                return isAvailable;
                              })
                              .map(did => (
                                <option key={did._id} value={did._id}>
                                  {did.number}
                                </option>
                              ))}
                          </select>
                          
                          {/* Helper list showing assigned DIDs */}
                          <div className="text-xs text-gray-500 mt-1 hidden">
                            {dids
                              .filter(did => did.assignedToUsername)
                              .map(did => (
                                <div key={`assigned-${did._id}`}>
                                  DID {did.number} assigned to: {did.assignedToUsername}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SMS Limit Management */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Monthly SMS Limit</label>
                    <div className="mt-1 flex flex-col space-y-2">
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          min="0"
                          value={tempSmsLimits[user._id] ?? (user.smsLimit ?? '')}
                          placeholder="Unlimited"
                          onChange={(e) => handleSmsLimitChange(user._id, e.target.value)}
                          className="flex-1 min-w-0 block px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        <input
                          type="date"
                          value={tempStartDates[user._id] ?? formatDate(user.smsStartDate) ?? formatDate(new Date())}
                          onChange={(e) => handleStartDateChange(user._id, e.target.value)}
                          className="block px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          min={formatDate(new Date())}
                        />
                        <button
                          onClick={() => handleUpdateSMSLimit(user._id)}
                          disabled={actionLoading[`smsLimit_${user._id}`]}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {actionLoading[`smsLimit_${user._id}`] ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                      
                      {/* SMS Usage Display */}
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full ${
                              user.smsLimit && (user.smsUsage?.monthly || 0) >= user.smsLimit
                                ? 'bg-red-600'
                                : 'bg-blue-600'
                            }`}
                            style={{
                              width: `${user.smsLimit ? Math.min(((user.smsUsage?.monthly || 0) / user.smsLimit) * 100, 100) : 0}%`
                            }}
                          ></div>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          {user.smsUsage?.monthly || 0} of {user.smsLimit ? `${user.smsLimit} SMS` : 'Unlimited'} used
                        </p>
                      </div>
                      
                      {/* Cycle Information */}
                      <div className="hidden text-sm text-gray-500">
                        <p>Cycle Start: {formatDisplayDate(user.smsStartDate)}</p>
                        <p>Next Reset: {formatDisplayDate(user.smsResetDate)}</p>
                        {user.smsLimit === null && (
                          <p className="italic">Unlimited SMS - no cycle restrictions</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {hasMore && (
                <button
                  onClick={handleLoadMore}
                  className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-800 mx-auto"></div>
                  ) : (
                    'Load More'
                  )}
                </button>
              )}
            </div>
          </div>

          {/* DIDs Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">DIDs</h2>
              <button
                onClick={handleRepairAllDIDs}
                disabled={actionLoading.repairAllDIDs}
                className="text-sm px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md flex items-center hidden"
                title="Repair all DID assignments"
              >
                {actionLoading.repairAllDIDs ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <i className="fas fa-tools mr-2"></i>
                )}
                Repair All DIDs
              </button>
            </div>
            
            {/* Create DID Form */}
            <form onSubmit={handleCreateDID} className="mb-6">
              <div className="flex space-x-2">
                <input
                  id="newDidNumber"
                  type="text"
                  value={newDid.number}
                  onChange={(e) => setNewDid({ number: e.target.value })}
                  placeholder="1234567890"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                  data-tooltip-id="did-tooltip"
                  data-tooltip-content="Enter a 10-digit number"
                />
                <Tooltip id="did-tooltip" />
                <button
                  type="submit"
                  disabled={actionLoading.createDid}
                  className={`${
                    actionLoading.createDid ? 'bg-blue-400' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center`}
                >
                  {actionLoading.createDid ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Add DID'
                  )}
                </button>
              </div>
            </form>

            {/* DIDs List */}
            <div className="space-y-4">
              {dids.map((did) => (
                <div key={did._id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold">{did.number}</p>
                      <p className="text-sm text-gray-500">
                        Status: {did.assignedTo ? (did.assignedToUsername ? `Assigned to ${did.assignedToUsername}` : 'Assigned (orphaned)') : 'Available'}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      {/* Show Fix button for DIDs that are marked as assigned but don't have an assignedToUsername */}
                      {did.assignedTo && !did.assignedToUsername && (
                        <button
                          onClick={() => handleFixOrphanedDID(did._id, did.number)}
                          className="text-yellow-600 hover:text-yellow-800 p-1"
                          title="Fix orphaned assignment"
                          disabled={actionLoading[`fixDid_${did.number}`]}
                          data-tooltip-id={`fix-did-tooltip-${did._id}`}
                          data-tooltip-content="Fix orphaned DID assignment"
                        >
                          {actionLoading[`fixDid_${did.number}`] ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600"></div>
                          ) : (
                            <i className="fas fa-wrench text-lg"></i>
                          )}
                        </button>
                      )}
                      <Tooltip id={`fix-did-tooltip-${did._id}`} />
                      
                      <button
                        onClick={() => {
                          setDidToDelete(did);
                          setShowDeleteDidConfirm(true);
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete DID"
                        disabled={actionLoading[`deleteDid_${did.number}`] || did.assignedTo}
                        data-tooltip-id={`delete-did-tooltip-${did._id}`}
                        data-tooltip-content={did.assignedTo ? "Cannot delete assigned DID" : "Delete DID"}
                      >
                        {actionLoading[`deleteDid_${did.number}`] ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                        ) : (
                          <i className={`fas fa-trash text-lg ${did.assignedTo ? 'opacity-50' : ''}`}></i>
                        )}
                      </button>
                      <Tooltip id={`delete-did-tooltip-${did._id}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Change Password</h3>
            <form onSubmit={handleChangePassword}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
                <PasswordRequirements password={passwordData.newPassword} />
                <p className="text-xs text-gray-500 mt-2">
                  As an admin you can set this without knowing the user's current password.
                </p>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setPasswordData({ userId: '', newPassword: '' });
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading.changePassword || !isPasswordValid(passwordData.newPassword)}
                  className={`px-4 py-2 ${
                    actionLoading.changePassword || !isPasswordValid(passwordData.newPassword)
                      ? 'bg-blue-300 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md flex items-center`}
                >
                  {actionLoading.changePassword ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Delete User</h3>
            <p className="text-red-600 mb-4">
              Warning: This will permanently delete the user "{userToDelete.username}" and all their conversations. This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(userToDelete._id)}
                disabled={actionLoading[`delete_${userToDelete._id}`]}
                className={`px-4 py-2 ${
                  actionLoading[`delete_${userToDelete._id}`] ? 'bg-red-400' : 'bg-red-500 hover:bg-red-600'
                } text-white rounded-md flex items-center`}
              >
                {actionLoading[`delete_${userToDelete._id}`] ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete DID Confirmation Modal */}
      {showDeleteDidConfirm && didToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Delete DID</h3>
            <p className="text-red-600 mb-4">
              Warning: This will permanently delete the DID "{didToDelete.number}". This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowDeleteDidConfirm(false);
                  setDidToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDID(didToDelete.number)}
                disabled={actionLoading[`deleteDid_${didToDelete.number}`]}
                className={`px-4 py-2 ${
                  actionLoading[`deleteDid_${didToDelete.number}`] ? 'bg-red-400' : 'bg-red-500 hover:bg-red-600'
                } text-white rounded-md flex items-center`}
              >
                {actionLoading[`deleteDid_${didToDelete.number}`] ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminDashboard;
